import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { supabase } from "@/integrations/supabase/client";
import { useAdminStatsGeneration } from "./useAdminStatsGeneration";

export type Timeframe = "1d" | "1w" | "1m" | "3m" | "1y" | "all";

const TIMEFRAME_OPTIONS = ["1d", "1w", "1m", "3m", "1y", "all"] as const;

const TIMEFRAME_LABELS: Record<Timeframe, string> = {
  "1d": "24h",
  "1w": "7 days",
  "1m": "30 days",
  "3m": "3 months",
  "1y": "1 year",
  "all": "All time",
};

export const TIMEFRAMES: { value: Timeframe; label: string }[] = [
  { value: "1d", label: "1D" },
  { value: "1w", label: "1W" },
  { value: "1m", label: "1M" },
  { value: "3m", label: "3M" },
  { value: "1y", label: "1Y" },
  { value: "all", label: "All" },
];

const STALE_THRESHOLD_MS = 60 * 1000; // 1 minute
const STORAGE_KEY = "admin-user-stats-timeframe";

const getInitialTimeframe = (): Timeframe => {
  if (typeof window === "undefined") return "1m";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && TIMEFRAME_OPTIONS.includes(stored as Timeframe)) {
    return stored as Timeframe;
  }
  return "1m";
};

export const useUserStats = () => {
  const [urlTimeframe, setUrlTimeframe] = useQueryState(
    "userTf",
    parseAsStringLiteral(TIMEFRAME_OPTIONS).withDefault(getInitialTimeframe())
  );
  
  const timeframe = urlTimeframe as Timeframe;
  const { generateStatsBackground, isGenerating } = useAdminStatsGeneration();
  const hasTriggeredRefresh = useRef(false);

  // Persist to localStorage when timeframe changes
  const setTimeframe = useCallback((newTimeframe: Timeframe) => {
    localStorage.setItem(STORAGE_KEY, newTimeframe);
    setUrlTimeframe(newTimeframe);
  }, [setUrlTimeframe]);

  const { data: cachedData, isLoading } = useQuery({
    queryKey: ["user-stats-cache", timeframe],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_stats_cache")
        .select("stats, chart_data, generated_at")
        .eq("timeframe", timeframe)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
  });

  // Auto-refresh if data is stale (older than 1 minute)
  useEffect(() => {
    if (!cachedData?.generated_at || isGenerating("user") || hasTriggeredRefresh.current) {
      return;
    }

    const generatedTime = new Date(cachedData.generated_at).getTime();
    const now = Date.now();
    const isStale = now - generatedTime > STALE_THRESHOLD_MS;

    if (isStale) {
      hasTriggeredRefresh.current = true;
      generateStatsBackground("user");
    }
  }, [cachedData?.generated_at, isGenerating, generateStatsBackground]);

  // Reset refresh trigger when timeframe changes
  useEffect(() => {
    hasTriggeredRefresh.current = false;
  }, [timeframe]);

  return {
    timeframe,
    setTimeframe,
    stats: cachedData?.stats as {
      total: number;
      totalCredits: number;
      usersWithCredits: number;
      unlimitedActive: number;
      unlimitedExpired: number;
      admins: number;
      hosts: number;
      referredUsers: number;
      activeUsers: number;
    } | null,
    chartData: (cachedData?.chart_data as {
      usersOverTime: { date: string; users: number }[];
      memberTypes: { type: string; count: number }[];
     topReferrers: { id: string; name: string; referralCount: number; referredUsersBooked: number; totalEventsBooked: number }[];
   }) || { usersOverTime: [], memberTypes: [], topReferrers: [] },
    generatedAt: cachedData?.generated_at,
    isLoading,
    isRefreshing: isGenerating("user"),
    timeframeLabel: TIMEFRAME_LABELS[timeframe],
  };
};
